import type { ComponentProps } from 'react';
import React, { Children, cloneElement, isValidElement, useState } from 'react';

import { cn } from '../cn';
import type { Size } from '../Input2';
import TableCellHeader from '../TableCellHeader';
import { WaveSpinner } from '../WaveSpinner';

type TableProps = Omit<ComponentProps<'div'>, 'size'> & {
  gridTemplateColumns: string;
  bordered?: boolean;
  size?: Size;
};

export const Table = ({ bordered, gridTemplateColumns, children, className, size }: TableProps) => {
  const [hoverId, setHoverId] = useState<React.Key | null>();

  return (
    <div
      data-table-size={size}
      className={cn(
        'relative grid grow auto-rows-max',
        'shadow-[0_1px_3px_hsla(220,43%,11%,0.102),0_1px_2px_hsla(220,43%,11%,0.06)]',
        bordered && 'overflow-auto rounded-[10px]',
        bordered && size === 'sm' && 'shadow-none',
        className,
      )}
      style={{ gridTemplateColumns }}>
      {Children.map(children, (child) => {
        if (isValidElement<CellProps>(child)) {
          if (child.type === TableCellHeader) {
            return cloneElement(child, {
              onMouseEnter: () => setHoverId?.(child.key),
              onMouseLeave: () => setHoverId?.(null),
            });
          }
          return cloneElement(child, {
            hover: hoverId === child.key,
            onMouseEnter: () => setHoverId?.(child.key),
            onMouseLeave: () => setHoverId?.(null),
          });
        } else {
          return child;
        }
      })}
    </div>
  );
};

type Table2Props = ComponentProps<'div'> & {
  gridTemplateColumns: string;
  /** When true, rows stack vertically when container is narrow */
  responsiveStack?: boolean;
};
export const Table2 = ({ gridTemplateColumns, responsiveStack, children, className }: Table2Props) => {
  if (responsiveStack) {
    return (
      <div className="@container">
        <div
          className={cn(
            'grid grow relative overflow-auto',
            'border border-gray-200 dark:border-gray-800',
            'bg-white dark:bg-gray-900 rounded-lg shadow-xs',
            'grid-cols-1 @[800px]:grid-cols-[max-content_1fr]',
            className,
          )}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'grid grow relative overflow-auto',
        'border border-gray-200 dark:border-gray-800',
        'bg-white dark:bg-gray-900 rounded-lg shadow-xs',
        className,
      )}
      style={{ gridTemplateColumns }}>
      {children}
    </div>
  );
};

type CellProps = ComponentProps<'div'> & {
  index: number;
  active?: boolean;
  hover?: boolean;
  test?: boolean;
};
export const Cell = ({ index, active, children, hover, onClick, className, ...props }: CellProps) => {
  const hoverable = onClick != null;
  return (
    <div
      className={cn(
        'flex flex-row items-center gap-1 text-sm font-normal transition-colors duration-200',
        'bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-100',
        'border-t border-gray-200 dark:border-gray-700',
        'px-6 py-5 in-data-[table-size=sm]:px-3 in-data-[table-size=sm]:py-2.5',
        index === 0 && 'border-t-0',
        active && 'bg-slate-50 dark:bg-[#3e5c98]',
        hoverable && 'cursor-pointer hover:bg-blue-700/5 hover:text-blue-700 dark:hover:bg-blue-400/15 dark:hover:text-blue-300',
        hover && 'bg-blue-700/5 text-blue-700 dark:bg-blue-400/15 dark:text-blue-300',
        className,
      )}
      onClick={onClick}
      {...props}>
      {children}
    </div>
  );
};

type Cell2Props = ComponentProps<'div'>;
export const Cell2 = ({ children, onClick, className, ...props }: Cell2Props) => {
  return (
    <div className={cn('flex flex-row items-center gap-1 text-sm font-normal', className)} onClick={onClick} {...props}>
      {children}
    </div>
  );
};

type RowProps = ComponentProps<'div'> & {
  active?: boolean;
  /** When true in a responsiveStack Table2, cells stack vertically when container is narrow */
  responsiveStack?: boolean;
};
export const Row = ({ children, onClick, className, active, responsiveStack, ...props }: RowProps) => {
  return (
    <div
      className={cn(
        'relative col-span-full grid group border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden',
        'first:rounded-t-lg last:rounded-b-lg first:border-t-0',
        responsiveStack ? 'grid-cols-1 @[800px]:grid-cols-subgrid' : 'grid-cols-subgrid',
        {
          'cursor-pointer hover:bg-blue-100/5 hover:text-blue-700': onClick,
          'bg-blue-100/5 text-blue-700 dark:bg-blue-900/5 dark:text-blue-300': active,
        },
        className,
      )}
      onClick={onClick}
      {...props}>
      {children}
    </div>
  );
};

type CellLoadingProps = ComponentProps<'div'> & {
  index: number;
};
export const CellLoading = ({ children, index }: CellLoadingProps) => {
  return (
    <div
      className={cn(
        'flex flex-row items-baseline justify-center gap-1 text-sm font-normal',
        'col-start-1 -col-end-1',
        'bg-white text-slate-900 dark:bg-slate-900 dark:text-white',
        'border-t border-gray-200 dark:border-gray-700',
        'px-6 py-5 in-data-[table-size=sm]:px-3 in-data-[table-size=sm]:py-2.5',
        index === 0 && 'border-t-0',
      )}>
      <div className="size-4 rounded-full bg-slate-200 text-blue-700 dark:bg-slate-700 [&>svg]:block [&>svg]:size-full">
        <WaveSpinner />
      </div>
      {children}
    </div>
  );
};
